import React, { useEffect, useState } from "react";
import {
    Button,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    CustomInput
} from "reactstrap";
import { ExtremeReadingMessage } from "../../../constants/general.constant";
import { useFetch2 } from "components/utils/authenticatedHook";
import _ from "lodash";

function EditExtremeReading({ show, onClose, initialValues = {}, userId }) {
    const [{ response, error, isLoading }, doFetch, sendError] = useFetch2();
    const [formData, setFormData] = useState({
        name: initialValues.name || "",
        condition: initialValues.condition || null,
        extremeReadingPatientMessage:
            initialValues.extremeReadingPatientMessage ||
            ExtremeReadingMessage.extremeReadingPatient,
        extremeReadingCareProviderMessage:
            initialValues.extremeReadingCareProviderMessage ||
            ExtremeReadingMessage.extremeReadingCareProvider,
        extremeReadingOneHourCareProviderMessage:
            initialValues.extremeReadingOneHourCareProviderMessage ||
            ExtremeReadingMessage.extremeReadingOneHourCareProvider,
        notifyReadingOwner: initialValues.notifyReadingOwner || [],
        notifySelf: initialValues.notifySelf || [],
        notifyStaff: initialValues.notifyStaff || [],
        valueProperty: initialValues.valueProperty || [],
    });

    const [formError, setFormError] = useState({});
    const [submitted, setSubmitted] = useState(false);

    const analyzeByValue =
        typeof formData.condition === "object" && formData.condition
            ? formData.condition.value
            : formData.condition;

    const selectedSubTypes = Array.isArray(formData.valueProperty)
        ? formData.valueProperty.map((v) => v)
        : [];

    const validateForm = () => {
        const errors = {};
        const numberRegex = /^\d+(\.\d+)?$/;

        if (!formData.name || !formData.name.trim()) errors.name = "Name is required";
        if (!formData.condition && initialValues.readingType && initialValues.readingType._id !== "5bb306382598931ffbd1b625") errors.condition = "Analyze type is required";

        selectedSubTypes.forEach((sub) => {
            const min = sub.minValue;
            const max = sub.maxValue;
            const val = sub.value;
            const gainLossPerDay = sub.gainLossPerDay;
            if(initialValues.readingType && initialValues.readingType._id == "5bb306382598931ffbd1b625"){
            if (!gainLossPerDay) {
                errors[`analyzeValue_${sub._id}`] = "Weight is required";
            }
            }else{
            if (analyzeByValue === "between") {
                if (!min || !max) {
                    errors[`analyzeValue_${sub._id}`] = "Both min and max values are required";
                } else if (!numberRegex.test(min) || !numberRegex.test(max)) {
                    errors[`analyzeValue_${sub._id}`] = "Only numeric values are allowed";
                } else if (parseFloat(max) <= parseFloat(min)) {
                    errors[`analyzeValue_${sub._id}`] = "Max must be greater than Min";
                }
            } else {
                if (!val) {
                    errors[`analyzeValue_${sub._id}`] = "Value is required";
                } else if (!numberRegex.test(val)) {
                    errors[`analyzeValue_${sub._id}`] = "Only numeric value is allowed";
                }
            }
          }
        });

        if (!formData.extremeReadingPatientMessage?.trim()) {
            errors.extremeReadingPatientMessage = "Patient message is required";
        }
        if (!formData.extremeReadingCareProviderMessage?.trim()) {
            errors.extremeReadingCareProviderMessage = "Care provider message is required";
        }
        if (!formData.extremeReadingOneHourCareProviderMessage?.trim()) {
            errors.extremeReadingOneHourCareProviderMessage = "One hour care provider message is required";
        }
        if (!formData.notifyReadingOwner?.length) {
            errors.notifyReadingOwner = "Select at least one notification method for Reading Owner";
        }
        if (!formData.notifyStaff?.length) {
            errors.notifyStaff = "Select at least one notification method for Self";
        }
        return errors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        const errors = validateForm();
        setFormError(errors);
        if (Object.keys(errors).length === 0) {
            const updatedData = {
                name: formData.name,
                condition: analyzeByValue,
                valueProperty: formData.valueProperty,
                extremeReadingPatientMessage: formData.extremeReadingPatientMessage,
                extremeReadingCareProviderMessage: formData.extremeReadingCareProviderMessage,
                extremeReadingOneHourCareProviderMessage:
                    formData.extremeReadingOneHourCareProviderMessage,
                notifyReadingOwner: formData.notifyReadingOwner,
                notifySelf: formData.notifySelf,
                id: initialValues._id,
                patientId: userId,
                parentId: initialValues?.parentId || null,
                notifyStaff: formData.notifyStaff,
            };
            doFetch({
                url: "/create-indiviual-extreme-expectations",
                method: "PUT",
                disableWait: true,
                body: updatedData,
            });
            onClose();
            const params = new URLSearchParams(window.location.search);
            params.set('tab', 7);
            // Push new query string to browser URL (without reload)
            window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
            window.location.reload();
        } 
    };

    const handleCheck = (e) => {
        const { name, value, checked } = e.target;
        const updated = checked
            ? [...formData[name], value]
            : formData[name].filter((v) => v !== value);
        setFormData((prev) => ({ ...prev, [name]: updated }));
    };

    useEffect(() => {
        if (initialValues) {
            setFormData({
                name: initialValues.name || "",
                condition: initialValues.condition || null,
                extremeReadingPatientMessage:
                    initialValues.extremeReadingPatientMessage ||
                    ExtremeReadingMessage.extremeReadingPatient,
                extremeReadingCareProviderMessage:
                    initialValues.extremeReadingCareProviderMessage ||
                    ExtremeReadingMessage.extremeReadingCareProvider,
                extremeReadingOneHourCareProviderMessage:
                    initialValues.extremeReadingOneHourCareProviderMessage ||
                    ExtremeReadingMessage.extremeReadingOneHourCareProvider,
                notifyReadingOwner: initialValues.notifyReadingOwner || [],
                notifySelf: initialValues.notifySelf || [],
                valueProperty: initialValues.valueProperty || [],
                notifyStaff: initialValues.notifyStaff || [],
            })
        }
    }, [initialValues])
  
    return (
        <Modal isOpen={show} toggle={onClose}>
            <ModalHeader toggle={onClose}>Update Event Expectation</ModalHeader>
            <ModalBody className="bg-white">
                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label>Expectation Name</Label>
                        <Input
                            className="input-date-filter"
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                        {submitted && formError.name && <div style={{ color: "red" }}>{formError.name}</div>}
                    </FormGroup>

                    {(initialValues.readingType && initialValues.readingType._id) !== "5bb306382598931ffbd1b625" && (<FormGroup>
                        <Label>Analyze By</Label>
                        <Input
                            type="select"
                            value={analyzeByValue}
                            onChange={(e) => {
                                const cleaned = Object.fromEntries(
                                    Object.entries(formData).filter(
                                        ([key]) =>
                                            !key.startsWith("minValue_") &&
                                            !key.startsWith("maxValue_") &&
                                            !key.startsWith("value_")
                                    )
                                );
                                setFormData({ ...cleaned, condition: e.target.value });
                            }}
                        >
                            <option value="">Select</option>
                            <option value=">=">Greater Than</option>
                            <option value="<=">Less Than</option>
                            <option value="between">In Range</option>
                        </Input>
                        {submitted && formError.condition && <div style={{ color: "red" }}>{formError.condition}</div>}
                    </FormGroup>)}

                    {selectedSubTypes.map((sub) => (
                        <FormGroup key={sub._id}>
                            <Label>{sub.name}</Label>
                           {(initialValues.readingType && initialValues.readingType._id) === "5bb306382598931ffbd1b625" ? (
                            <Input
                                type="number"
                                className="input-date-filter"
                                placeholder="Weight"
                                min={0}
                                value={
                                    Array.isArray(formData.valueProperty)
                                    ? formData.valueProperty.find(obj => obj.readingSubTypeId === sub.readingSubTypeId)?.gainLossPerDay ?? ""
                                    : ""
                                }
                                onChange={(e) => {
                                    const newMin = e.target.value;
                                    if (/^\d*\.?\d*$/.test(newMin)) {
                                    setFormData((prev) => {
                                        const updatedValue = Array.isArray(prev.valueProperty) ? [...prev.valueProperty] : [];
                                        const index = updatedValue.findIndex(obj => obj.readingSubTypeId === sub.readingSubTypeId);
                                        if (index !== -1) {
                                        updatedValue[index].gainLossPerDay = newMin;
                                        } else {
                                        updatedValue.push({
                                            readingSubTypeId: sub.readingSubTypeId,
                                            gainLossPerDay: newMin,
                                            maxValue: "",
                                            value: "",
                                            name: sub.name,
                                            maxValue: ""
                                        });
                                        }
                                        return {
                                        ...prev,
                                        valueProperty: updatedValue,
                                        };
                                    });
                                    }
                                }}
                                style={{ width: 120 }}
                            />
                            ) : analyzeByValue === "between" ? (
                            <div className="d-flex gap-2">
                                {/* Min Input */}
                                <Input
                                className="input-date-filter"
                                type="text"
                                placeholder="Min"
                                value={
                                    Array.isArray(formData.valueProperty)
                                    ? formData.valueProperty.find(obj => obj.readingSubTypeId === sub.readingSubTypeId)?.minValue ?? ""
                                    : ""
                                }
                                onChange={(e) => {
                                    const newMin = e.target.value;
                                    if (/^\d*\.?\d*$/.test(newMin)) {
                                    setFormData((prev) => {
                                        const updatedValue = Array.isArray(prev.valueProperty) ? [...prev.valueProperty] : [];
                                        const index = updatedValue.findIndex(obj => obj.readingSubTypeId === sub.readingSubTypeId);
                                        if (index !== -1) {
                                        updatedValue[index].minValue = newMin;
                                        } else {
                                        updatedValue.push({
                                            readingSubTypeId: sub.readingSubTypeId,
                                            minValue: newMin,
                                            maxValue: "",
                                            value: "",
                                            name: sub.name,
                                        });
                                        }
                                        return {
                                        ...prev,
                                        valueProperty: updatedValue,
                                        };
                                    });
                                    }
                                }}
                                />

                                {/* Max Input */}
                                <Input
                                className="input-date-filter ml-2"
                                type="text"
                                placeholder="Max"
                                value={
                                    Array.isArray(formData.valueProperty)
                                    ? formData.valueProperty.find(obj => obj.readingSubTypeId === sub.readingSubTypeId)?.maxValue ?? ""
                                    : ""
                                }
                                onChange={(e) => {
                                    const newMax = e.target.value;
                                    if (/^\d*\.?\d*$/.test(newMax)) {
                                    setFormData((prev) => {
                                        const updatedValue = Array.isArray(prev.valueProperty) ? [...prev.valueProperty] : [];
                                        const index = updatedValue.findIndex(obj => obj.readingSubTypeId === sub.readingSubTypeId);
                                        if (index !== -1) {
                                        updatedValue[index].maxValue = newMax;
                                        } else {
                                        updatedValue.push({
                                            readingSubTypeId: sub.readingSubTypeId,
                                            maxValue: newMax,
                                            minValue: "",
                                            value: "",
                                            name: sub.name,
                                        });
                                        }
                                        return {
                                        ...prev,
                                        valueProperty: updatedValue,
                                        };
                                    });
                                    }
                                }}
                                />
                            </div>
                            ) : (
                            <Input
                                className="input-date-filter"
                                type="text"
                                placeholder="Value"
                                value={
                                Array.isArray(formData.valueProperty)
                                    ? formData.valueProperty.find(obj => obj.readingSubTypeId === sub.readingSubTypeId)?.value ?? ""
                                    : ""
                                }
                                onChange={(e) => {
                                const newValue = e.target.value;
                                if (/^\d*\.?\d*$/.test(newValue)) {
                                    setFormData(prev => {
                                    const updatedValue = Array.isArray(prev.valueProperty) ? [...prev.valueProperty] : [];
                                    const index = updatedValue.findIndex(obj => obj.readingSubTypeId === sub.readingSubTypeId);
                                    if (index !== -1) {
                                        updatedValue[index].value = newValue;
                                    } else {
                                        updatedValue.push({
                                        readingSubTypeId: sub.readingSubTypeId,
                                        value: newValue,
                                        minValue: "",
                                        maxValue: "",
                                        name: sub.name,
                                        });
                                    }
                                    return {
                                        ...prev,
                                        valueProperty: updatedValue,
                                    };
                                    });
                                }
                                }}
                            />
                            )}
                            {submitted && formError[`analyzeValue_${sub._id}`] && (
                                <div style={{ color: "red" }}>{formError[`analyzeValue_${sub._id}`]}</div>
                            )}
                        </FormGroup>
                    ))}

                    {/* Textarea Fields */}
                    {[
                        {
                            label: "Patient Message",
                            key: "extremeReadingPatientMessage",
                            error: formError.extremeReadingPatientMessage,
                        },
                        {
                            label: "If patient retest and still reading extreme message for care provider",
                            key: "extremeReadingCareProviderMessage",
                            error: formError.extremeReadingCareProviderMessage,
                        },
                        {
                            label: "If patient not taking any reading in one hour then notify message for care provider",
                            key: "extremeReadingOneHourCareProviderMessage",
                            error: formError.extremeReadingOneHourCareProviderMessage,
                        }
                    ].map(({ label, key, error }) => (
                        <FormGroup key={key}>
                            <Label>{label}</Label>
                            <Input
                                className="input-date-filter"
                                type="textarea"
                                rows={10}
                                value={formData[key]}
                                onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                            />
                            {submitted && error && <div style={{ color: "red" }}>{error}</div>}
                        </FormGroup>
                    ))}

                    {/* Notification Checkboxes */}
                    {["notifySelf", "notifyStaff" ,"notifyReadingOwner"].map((field, index) => (
                        <FormGroup key={field}>
                            <Label>{field === "notifySelf" ? "Notify Self" : field === "notifyStaff" ? "Notify Staff"  :"Notify Readings Owner"}</Label>
                            <div>
                                {["inbox", "sms", "voice", "email"].map((type) => (
                                    <CustomInput
                                        key={`${field}_${type}`}
                                        type="checkbox"
                                        id={`${field}_${type}`}
                                        name={field}
                                        value={type}
                                        label={type.toUpperCase()}
                                        inline
                                        onChange={handleCheck}
                                        checked={_.includes(formData[field], type)}
                                    />
                                ))}
                            </div>
                            {submitted && formError[field] && <div style={{ color: "red" }}>{formError[field]}</div>}
                        </FormGroup>
                    ))}

                    <Button type="submit" className="btn btn-block btn-info save-click-cls">
                        <i className="mdi mdi-content-save"></i> Update
                    </Button>
                </Form>
            </ModalBody>
        </Modal>
    );
}

export default EditExtremeReading;
